import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import LoginForm from "../components/LoginForm";
import SignupForm from "../components/SignupForm";
import OtpVerificationForm from "../components/OtpVerificationForm";
import ForgotPasswordForm from "../components/ForgotPasswordForm";
import ResetPasswordForm from "../components/ResetPasswordForm";
import { useSession } from "../hooks/useSession";
import { AUTH_VIEWS, AUTH_STORAGE_KEYS } from "../constants/authentication.constants";
import "./authentication.css";

/**
 * Auth page hosting login, signup, OTP, and password-reset flows, plus
 * the redirect handling for Google OAuth and emailed confirmation/reset
 * links (including cross-tab session sync when a link opens in a new tab).
 */
export default function AuthenticationPage({ onAuthSuccess }) {
    const [activeView, setActiveView] = useState(AUTH_VIEWS.LOGIN);
    const [pendingEmail, setPendingEmail] = useState(null);
    const [resetToken, setResetToken] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { establishSession } = useSession();

    useEffect(() => {
        const view = new URLSearchParams(location.search).get("view");
        if (view === AUTH_VIEWS.SIGNUP) setActiveView(AUTH_VIEWS.SIGNUP);
    }, [location.search]);

    // Handles the OAuth (Google) redirect, which returns with ?token=... in
    // the URL -- and, when SocialAuthButtons.jsx sent one, a `returnState`
    // query param carrying wherever the password/OTP path's own
    // location.state would have pointed (redirectTo/pendingQuery/
    // openGuidedSearch/entryPoint), JSON-stringified by the backend's OAuth
    // callback (oauth.py's user_oauth_callback). Without this, Google
    // sign-in always landed on the plain homepage regardless of why the
    // person was sent to log in -- found live via the "Talk to Me" pill.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");

        if (token) {
            establishSession(token);
            const returnStateRaw = params.get("returnState");
            window.history.replaceState({}, "", window.location.pathname);
            onAuthSuccess?.();

            let returnState = null;
            if (returnStateRaw) {
                try { returnState = JSON.parse(returnStateRaw); } catch { /* malformed -- ignore */ }
            }
            const { redirectTo, pendingQuery, openGuidedSearch, entryPoint } = returnState || {};
            if (redirectTo) {
                navigate(redirectTo, { state: { question: pendingQuery, openGuidedSearch, entryPoint }, replace: true });
            } else {
                navigate("/", { replace: true });
            }
        }
    }, [establishSession, navigate, onAuthSuccess]);

    // After Google sign-in, GoogleUserAuthSuccessHandler emails a one-time
    // confirmation link instead of logging in immediately, and redirects
    // here with ?checkEmail=1&email=... so we can show an interstitial
    // instead of the login form. The session itself is only established
    // once the person opens that email and clicks the link (which lands on
    // the ?token=... path above).
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("checkEmail") === "1") {
            setPendingEmail(params.get("email") || "");
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    // Handles the "reset password" email link: GET .../auth?resetToken=...
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("resetToken");
        if (token) {
            setResetToken(token);
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    // Email links (Google sign-in confirmation, password reset) always open
    // in a NEW tab - the browser/email client controls that, not us - so
    // this tab is left stranded on "check your email" even after the user
    // finishes in the other tab. The `storage` event fires in every OTHER
    // same-origin tab whenever localStorage changes (never in the tab that
    // made the change), so once the new tab calls establishSession() and
    // writes the token, this tab picks it up automatically here and moves
    // on instead of requiring a manual switch-tabs-and-refresh.
    useEffect(() => {
        const handleStorage = (e) => {
            if (e.key === AUTH_STORAGE_KEYS.TOKEN && e.newValue) {
                onAuthSuccess?.();
                navigate("/", { replace: true });
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, [navigate, onAuthSuccess]);

    // If the person was bounced here from a gated feature (e.g. the AI
    // search bar), location.state carries where to send them back to
    // and what they were trying to do, so login doesn't lose their work.
    const handleSuccess = () => {
        onAuthSuccess?.();
        const { redirectTo, pendingQuery, openGuidedSearch, entryPoint } = location.state || {};

        if (redirectTo) {
            // `entryPoint` is forwarded alongside the pending query so a
            // gated entry point (e.g. the homepage "Talk to Me" pill) still
            // knows how it was reached once the person lands back on the
            // destination page after logging in.
            navigate(redirectTo, {
                state: { question: pendingQuery, openGuidedSearch, entryPoint },
                replace: true,
            });
        } else {
            navigate("/", { replace: true });
        }
    };

    const authReason = location.state?.authReason;

    const isTabView =
        activeView === AUTH_VIEWS.LOGIN || activeView === AUTH_VIEWS.SIGNUP;

    return (
        <div className="ogm-auth-page">
            <button
                type="button"
                className="ogm-auth-home-btn"
                onClick={() => navigate("/")}
                aria-label="Back to home"
            >
                ← Back to Home
            </button>

            <div className="ogm-auth-card">
                <div
                    className="ogm-auth-brand"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate("/")}
                    onKeyDown={(e) => e.key === "Enter" && navigate("/")}
                >
                    <img src="/logo.png" alt="OGM Logo" />
                    <span>One Global Marketplace</span>
                </div>

                {resetToken !== null ? (
                    <ResetPasswordForm
                        token={resetToken}
                        onSuccess={() => {
                            setResetToken(null);
                            setActiveView(AUTH_VIEWS.LOGIN);
                        }}
                    />
                ) : pendingEmail !== null ? (
                    <div className="ogm-check-email">
                        <h2>Check your email</h2>
                        <p>
                            We sent a sign-in link to <strong>{pendingEmail}</strong>.
                            Click the link to finish signing in with Google — it expires
                            in 10 minutes.
                        </p>
                        <p
                            className="ogm-back-link"
                            onClick={() => setPendingEmail(null)}
                        >
                            Back to login
                        </p>
                    </div>
                ) : (
                    <>
                        {authReason && <p className="ogm-auth-reason">{authReason}</p>}

                        {isTabView && (
                            <div className="ogm-auth-tabs" role="tablist">
                                {[AUTH_VIEWS.LOGIN, AUTH_VIEWS.SIGNUP].map((view) => (
                                    <button
                                        key={view}
                                        role="tab"
                                        aria-selected={activeView === view}
                                        className={activeView === view ? "active" : ""}
                                        onClick={() => setActiveView(view)}
                                    >
                                        {view === AUTH_VIEWS.LOGIN ? "Login" : "Sign Up"}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className={`ogm-auth-slider ogm-view-${activeView}`}>
                            {activeView === AUTH_VIEWS.LOGIN && (
                                <LoginForm
                                    onOtp={() => setActiveView(AUTH_VIEWS.OTP)}
                                    onForgotPassword={() => setActiveView(AUTH_VIEWS.FORGOT)}
                                    onSuccess={handleSuccess}
                                />
                            )}

                            {activeView === AUTH_VIEWS.SIGNUP && (
                                <SignupForm onSuccess={handleSuccess} />
                            )}

                            {activeView === AUTH_VIEWS.OTP && (
                                <OtpVerificationForm
                                    onBack={() => setActiveView(AUTH_VIEWS.LOGIN)}
                                    onSuccess={handleSuccess}
                                />
                            )}

                            {activeView === AUTH_VIEWS.FORGOT && (
                                <ForgotPasswordForm
                                    onBack={() => setActiveView(AUTH_VIEWS.LOGIN)}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}